import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Dark code block with a copy button — used for embed snippets, API examples and MCP config.
 * The copy button sits in its own header bar, so it never covers the code.
 */
export function CopyBlock({
  label,
  value,
  icon,
  hint,
  lang,
  mono = true,
}: {
  label?: string;
  value: string;
  icon?: ReactNode;
  hint?: ReactNode;
  /** Small tag in the header bar, e.g. "HTML", "URL", "Node.js". */
  lang?: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard can be blocked; the text stays selectable.
    }
  };
  return (
    <div className="min-w-0">
      {label && (
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
          {icon && <span className="text-brand-500">{icon}</span>}
          {label}
        </div>
      )}
      <div className="bg-ink-950 overflow-hidden rounded-xl ring-1 ring-black/5">
        <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
          <span className="flex gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </span>
          {lang && (
            <span className="ml-1 text-[11px] font-medium tracking-wide text-slate-400 uppercase">
              {lang}
            </span>
          )}
          <button
            type="button"
            onClick={() => void copy()}
            className={cn(
              'ml-auto flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              copied ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white hover:bg-white/20',
            )}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre
          className={cn(
            'overflow-x-auto p-4 text-[12.5px] leading-relaxed text-slate-100',
            mono && 'font-mono',
          )}
        >
          {value}
        </pre>
      </div>
      {hint && <p className="mt-2 text-xs leading-relaxed text-slate-500">{hint}</p>}
    </div>
  );
}
